import { useId } from "react";
import type { CardPool } from "../lib/deck-card-pool";
import { cn } from "../lib/cn";

interface Props {
  mode: CardPool;
  onChange: (mode: CardPool) => void;
  collectionName?: string;
  disabled?: boolean;
}

export default function DeckCardPoolControl({
  mode,
  onChange,
  collectionName,
  disabled = false,
}: Props) {
  const id = useId();
  const sourceLabel = collectionName?.trim() || "Original collection";

  return (
    <fieldset disabled={disabled} aria-describedby={`${id}-help`} className="space-y-2">
      <legend className="mb-2 text-xs text-fg-muted">Cards for suggestions</legend>
      <div className="flex flex-wrap gap-2">
        {([
          { value: "collection", label: sourceLabel },
          { value: "all", label: "All cards" },
        ] as const).map(({ value, label }) => (
          <label
            key={value}
            className={cn(
              "relative inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs transition-colors",
              "focus-within:ring-2 focus-within:ring-accent/50",
              mode === value
                ? "border-accent/40 bg-accent/20 text-accent"
                : "border-border text-fg-muted hover:text-fg",
              disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer",
            )}
          >
            <input
              type="radio"
              name={`${id}-pool`}
              value={value}
              checked={mode === value}
              onChange={() => onChange(value)}
              className="sr-only"
            />
            {label}
          </label>
        ))}
      </div>
      <p id={`${id}-help`} className="text-[11px] text-fg-subtle">
        {mode === "all"
          ? "Suggestions can include upgrades and extra copies you may need to buy."
          : "Suggestions use cards and copies you own in the original collection."}
      </p>
    </fieldset>
  );
}
